import { useScrollEdges } from '@/hooks/ui/useScrollEdges';

interface StepLayoutProps {
  title: string;
  description?: React.ReactNode;
  /** Sits above the title. The Complete screen's failure icon. */
  icon?: React.ReactNode;
  /** Centres the heading, and a lone action under it. */
  align?: 'start' | 'center';
  /**
   * `form` is the card frame: heading fixed at the top, actions fixed in a
   * footer at the bottom, and only the body between them scrolls - so the
   * title and the buttons hold still across the steps the user clicks through.
   *
   * `terminal` centres the whole group instead. An arrival screen is two lines
   * and a button; spread over a full card that is a heading, 500px of nothing,
   * and a button stranded at the bottom. It still FILLS the card — same box,
   * same width as every other step — it just doesn't pretend to be a form.
   */
  variant?: 'form' | 'terminal';
  /** The step's body, between the heading and the action row. */
  children?: React.ReactNode;
  /** Rendered at the LEFT of the action row. Forces a two-end row. */
  back?: React.ReactNode;
  /** The forward action. Right-aligned, or centred when `align='center'`. */
  actions?: React.ReactNode;
}

/**
 * The shape every onboarding step renders into: a fixed heading, a scrolling
 * body, and a fixed footer of actions.
 *
 * **Only the body scrolls.** The card is a fixed height (the wizard fills the
 * viewport under the brand bar), and a step can be taller than it: at 390x844
 * the lessons step overflows by 235px, and at 1280x900 the plans do. When the
 * whole step scrolled as one, the buttons sat at the end of it - below the
 * fold, with nothing on screen saying they existed - and a pinned-but-sticky
 * row still let content slide underneath it with the scrollbar running behind
 * it. Here the heading and the footer sit OUTSIDE the scroller, so the title
 * says where you are, the buttons are always one tap away, and the scrollbar
 * spans only what actually scrolls. A soft shadow (`useScrollEdges`) shows at
 * whichever edge of the body has more to see, which is the cue the fixed frame needs.
 *
 * The frame is also what keeps Back and Continue at the same height from step
 * to step - which matters here more than on a normal page, because the user
 * clicks them four times in a row. (It replaced `space-y-10 / 10 / 8 / 6`
 * blocks that each sized to their own content and landed their buttons at a
 * different height.)
 *
 * The regions carry the card's padding themselves, so the wizard's step
 * wrapper (and `withWizardCard` in the stories) is unpadded.
 *
 * `<h2>`, not `PageHeading`'s `<h1>`: the wizard is mounted inside a page that
 * may grow its own top-level heading, and the steps are sections of it.
 */
export function StepLayout({
  title,
  description,
  icon,
  align = 'start',
  variant = 'form',
  children,
  back,
  actions,
}: StepLayoutProps) {
  const terminal = variant === 'terminal';
  const centered = terminal || align === 'center';

  const heading = (
    <StepHeading
      title={title}
      description={description}
      icon={icon}
      centered={centered}
    />
  );
  const actionRow = (back || actions) && (
    <ActionRow back={back} actions={actions} centered={centered} />
  );

  // The arrival screen: two lines and a button, centred in the card. Nothing
  // to frame, so no fixed footer - but it still scrolls rather than clips if a
  // very short screen ever needs it to.
  if (terminal) {
    return (
      <div className='flex min-h-0 flex-1 flex-col justify-center gap-8 overflow-y-auto p-6 sm:p-10'>
        {heading}
        {children}
        {actionRow}
      </div>
    );
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='shrink-0 px-6 pb-6 pt-6 sm:px-10 sm:pb-8 sm:pt-10'>
        {heading}
      </div>
      <ScrollBody>{children}</ScrollBody>
      {actionRow && (
        // Below `sm` the buttons are 40px, and when there is a forward action
        // they split the footer 50/50 - full-width targets near the thumb,
        // drawn at a normal button height. Back on its own (the plan step,
        // whose way on is the plan cards) keeps its own width at the left.
        <div
          className={`shrink-0 border-t border-border/60 px-6 py-3 sm:px-10 sm:py-4 max-sm:[&>div>*]:h-10 ${actions ? 'max-sm:[&>div>*]:flex-1' : ''}`}
        >
          {actionRow}
        </div>
      )}
    </div>
  );
}

function StepHeading({
  title,
  description,
  icon,
  centered,
}: Pick<StepLayoutProps, 'title' | 'description' | 'icon'> & {
  centered: boolean;
}) {
  return (
    <div className={centered ? 'text-center' : undefined}>
      {icon}
      {/* Plain concatenation, never cn(): tailwind-merge treats this repo's
          custom fontSize keys as colours, so cn('text-title text-foreground')
          drops the size and renders at the inherited 16/400. */}
      <h2 className='text-title text-foreground'>{title}</h2>
      {description && (
        <p className='mt-1 text-body text-muted-foreground'>{description}</p>
      )}
    </div>
  );
}

function ActionRow({
  back,
  actions,
  centered,
}: Pick<StepLayoutProps, 'back' | 'actions'> & { centered: boolean }) {
  // With a Back button the row is always a two-end layout - an empty right
  // slot keeps Back at the left edge on the plan step, where the forward
  // action lives on the plan cards instead. Without one, the lone action sits
  // where the step's heading points: at the right edge normally, centred on a
  // centred screen.
  const justify = back
    ? 'justify-between'
    : centered
      ? 'justify-center'
      : 'justify-end';
  return (
    // `tap-target`: every step's Back / Continue is drawn at button height
    // and tapped as 44 on a phone (width-scoped in globals.css).
    <div className={`flex items-center gap-3 ${justify} [&>button]:tap-target`}>
      {back}
      {back ? (actions ?? <span />) : actions}
    </div>
  );
}

/** The step's only scroller, with a "there is more" shadow at each edge. */
function ScrollBody({ children }: { children?: React.ReactNode }) {
  const { ref, edges } = useScrollEdges<HTMLDivElement>();
  const shadow =
    'pointer-events-none absolute inset-x-0 h-5 from-foreground/[0.07] to-transparent transition-opacity duration-200';
  return (
    <div className='relative min-h-0 flex-1'>
      <div
        ref={ref}
        className='h-full overflow-y-auto px-6 pb-6 sm:px-10 sm:pb-10'
      >
        {/* One wrapper, so `useScrollEdges` can watch the content's height.
            `gap-10` is the space between a step's sections (the lessons step
            has two), which the step used to get from StepLayout's own column. */}
        <div className='flex flex-col gap-10'>{children}</div>
      </div>
      {/* Shown only at an edge with content past it; never takes a tap. */}
      <div
        aria-hidden
        className={`${shadow} top-0 bg-gradient-to-b ${edges.top ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        aria-hidden
        className={`${shadow} bottom-0 bg-gradient-to-t ${edges.bottom ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}
